/**
 * Missions store — the SPA's hydrated, polling-driven view of the per-user
 * mission-service sidecar (Phase b of the LexIDE Missions surface, Part D
 * of the Team-of-Agents handoff).
 *
 * Source of truth: mission-service. This store is a polled snapshot —
 * `useMissionsPoll` hydrates it on a 5s active / 30s idle cadence with
 * Page-Visibility gating. Persistence uses **sessionStorage**: the
 * snapshot survives in-tab reload/navigation but a fresh tab starts
 * empty and re-hydrates from the server (mirrors `useActiveContextStore`).
 *
 * Shape: a `Map<missionId, MissionSlot>` keyed by missionId. Map preserves
 * insertion order, so `selectQueuedMissions` returns the FIFO admission
 * queue without resort when the server already orders by chronological
 * ULID (router.ts:91). We still sort defensively by `openedAt` so a future
 * reorder doesn't break the queue UX.
 *
 * Persistence trick for `Map`: serialise via `Array.from(.entries())`,
 * rebuild via `new Map(entries)` in the `merge` callback. zustand's
 * default JSON storage cannot round-trip a `Map` natively.
 *
 * Case-switch reset: callers (typically the LexIDE shell) wire
 * `useActiveContextStore` → `setActiveCaseId(newCaseId)` + `clear()` when
 * the active case changes. Per-action `caseId` guard in `hydrateFromList`
 * also protects against late responses from a previous-case poll.
 *
 * Selectors return fresh arrays/objects — pair every store-hook usage
 * that subscribes to a derived collection with `useShallow` from
 * `zustand/react/shallow` so React only re-renders when the contents
 * actually change. Subscribing to the raw `s.missions` Map and computing
 * the derived value with `useMemo` is the other valid pattern; both are
 * used elsewhere in the codebase.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { StateStorage } from "zustand/middleware";
import { createTenantScopedStorage } from "@/lib/tenant-scoped-storage";

import type {
  MissionRecord,
  MissionState,
  MissionSummary,
  NodeError,
} from "@/api/lx-mission";

/** A poll-snapshot entry. We keep both the SUMMARY (returned by
 *  `GET /missions`) and OPTIONALLY the full record (filled by a
 *  `getMission` drill-in). Most pane consumers can render from the
 *  summary alone; the awaiting-user banner and the `awaiting_user_timeout`
 *  copy need `record.error?.code` so drill-in fills the full record on
 *  demand for those rows. */
export interface MissionSlot {
  summary: MissionSummary;
  /** Filled lazily by `useMissionsPoll.cancelMission` and by any future
   *  drill-in. `undefined` does NOT mean the mission is missing — only
   *  that the SPA hasn't fetched the full record yet. */
  record?: MissionRecord;
  /** Wall-clock ms of the last poll that touched this slot. */
  lastFetchedAt: number;
  /** Optimistic flag while a cancel request is in-flight. Cleared by the
   *  next poll that observes the terminal state OR by `removeMission`. */
  isCanceling?: boolean;
  /** Live overlay from the `mission.progress` push, keyed by nodeId.
   *
   *  The push arrives BEFORE the drill-in that would carry the same
   *  information on `record.nodes`, so the sidebar renders this on top of
   *  the record to move a step the instant the coordinator transitions.
   *  It is strictly an overlay: `record` stays the authoritative source
   *  for everything else (timings, cost, error, resultRef), and a stale
   *  overlay entry is harmless because the record catches up within one
   *  tick. Not persisted (see `partializeForPersist`) — live-only. */
  liveNodes?: Map<string, LiveNodeProgress>;
}

/** One node inside a `mission.progress` push payload. Mirrors the runtime
 *  broadcast shipped as [M:mission-chat-progress]; `label` is already
 *  resolved server-side to `node.summary ?? harnessId`. */
export interface MissionProgressNode {
  id: string;
  label?: string;
  state?: string;
  pct?: number;
  message?: string;
}

/** The `mission.progress` gateway event payload. */
export interface MissionProgressEvent {
  missionId?: string;
  sessionKey?: string;
  state?: MissionState;
  nodes?: MissionProgressNode[];
  progressPct?: number;
  ts?: number;
}

/** The overlay value stored per node. `order` preserves the payload's
 *  plan ordering so steps can be laid out even before `plan` has been
 *  drilled in. */
export interface LiveNodeProgress {
  label?: string;
  state?: string;
  pct?: number;
  message?: string;
  order: number;
  at: number;
}

interface MissionsState {
  /** `missionId → slot`. */
  missions: Map<string, MissionSlot>;
  /** The case the pane is currently scoped to (set by an App-level
   *  subscriber to `useActiveContextStore`). `null` means "no case
   *  selected" — pane shows the org-wide cross-case feed. */
  activeCaseId: string | null;
  /** Last polling error message — fail-soft surface for the pane. */
  pollError: string | null;
  /** Wall-clock ms of the last successful list poll. */
  lastPolledAt: number | null;
  /** S3 / S6: ID of a row to highlight + scroll-into-view briefly.
   *  Written by:
   *    - `HarnessRow` / `MissionRow` self-focus (§I.4) when the
   *      lawyer clicks a row body
   *    - `useTeamPaneCommandsFromAgent` (S6) when the concierge calls
   *      `lx_focus_mission(mission_id)`
   *  Cleared automatically by `focusMission`'s setTimeout after
   *  `FOCUS_PULSE_MS` (~2 s).
   *  When a standalone-harness task is focused, the value is the
   *  `task:<taskId>` form so the Map<id, string> selector
   *  disambiguates mission vs task. */
  focusedRowId: string | null;
}

interface MissionsActions {
  /** Replace the SUMMARY view of missions, filtered by case scope.
   *  `opts.caseId` (when provided) makes this call authoritative for
   *  that case: slots scoped to that case that are NOT in `summaries`
   *  are removed (server says "no longer in scope"). Slots for other
   *  cases are untouched. */
  hydrateFromList: (
    summaries: MissionSummary[],
    opts?: { caseId?: string },
  ) => void;
  /** Upsert a single full record (from `getMission`). Preserves the
   *  optimistic `isCanceling` flag if set. */
  upsertMission: (record: MissionRecord) => void;
  /** Merge a `mission.progress` push into the slot's live overlay so the
   *  sidebar's step rows advance without waiting for a drill-in. No-ops
   *  for an unknown missionId or a malformed payload (the event bus is
   *  an untrusted wire — a bad frame must never blank the pane). */
  applyMissionProgress: (payload: MissionProgressEvent) => void;
  /** Optimistic flag — set while a cancel request is in-flight. */
  markCanceling: (missionId: string) => void;
  /** Remove a mission outright (e.g. cancel-of-queued where the server
   *  may purge the row immediately, or admin tooling). */
  removeMission: (missionId: string) => void;
  /** Reset everything. Called on case-switch by the App-level subscriber. */
  clear: () => void;
  setActiveCaseId: (caseId: string | null) => void;
  setPollError: (msg: string | null) => void;
  setLastPolledAt: (ts: number) => void;
  /** Focus a row briefly — sets `focusedRowId` and clears it after
   *  the pulse window. Idempotent if called for the same row twice
   *  within the window (resets the timer). Pass `"mission:<id>"` or
   *  `"task:<id>"`. */
  focusRow: (rowId: string) => void;
}

export type MissionsStoreState = MissionsState & MissionsActions;

const INITIAL: MissionsState = {
  missions: new Map(),
  activeCaseId: null,
  pollError: null,
  lastPolledAt: null,
  focusedRowId: null,
};

/** Duration of the §I.4 focus-row highlight pulse (matches the
 *  `lx_focus_mission` ad-hoc nudge in §H.6). */
export const FOCUS_PULSE_MS = 2_000;

/** Single timer ref shared across `focusRow` calls so re-focusing
 *  the same row within the window resets the clock cleanly. Module-
 *  level so vitest's `clear()` doesn't leak across tests. */
let focusClearTimer: ReturnType<typeof setTimeout> | null = null;

// ─── Helpers (extracted for clarity + selector reuse) ─────────────────

function slotFromSummary(
  s: MissionSummary,
  prev: MissionSlot | undefined,
): MissionSlot {
  return {
    summary: s,
    record: prev?.record,
    lastFetchedAt: Date.now(),
    isCanceling: prev?.isCanceling,
    liveNodes: prev?.liveNodes,
  };
}

function slotFromRecord(
  r: MissionRecord,
  prev: MissionSlot | undefined,
): MissionSlot {
  const summary: MissionSummary = prev?.summary ?? {
    missionId: r.missionId,
    state: r.state,
    goal: r.goal,
    scope: r.scope,
    openedAt: r.openedAt,
    completedAt: r.completedAt,
  };
  // Keep the summary's state/goal/etc in lockstep with the freshest
  // record fetch — they share the same source of truth on the server.
  return {
    summary: {
      ...summary,
      state: r.state,
      goal: r.goal,
      scope: r.scope,
      openedAt: r.openedAt,
      completedAt: r.completedAt,
    },
    record: r,
    lastFetchedAt: Date.now(),
    isCanceling: prev?.isCanceling,
    liveNodes: prev?.liveNodes,
  };
}

// ─── The store ────────────────────────────────────────────────────────

/** Drop the live-only progress overlay before persisting a slot. */
function stripLiveNodes(slot: MissionSlot): MissionSlot {
  if (!slot.liveNodes) return slot;
  const { liveNodes: _dropped, ...rest } = slot;
  return rest;
}

/**
 * How many missions may ride in the PERSISTED snapshot.
 *
 * Persistence here is a reload convenience, not an archive: the poll refills
 * the pane from the server within a tick. Left unbounded it became an archive
 * anyway — a live tab was measured holding **2,856 missions / 4.94 MB** under
 * this one key, with sessionStorage sitting exactly on its ~5 MB ceiling. Every
 * subsequent write then threw `QuotaExceededError`, and because sessionStorage
 * SURVIVES A RELOAD the oversized blob rehydrated and the next write threw
 * again — the pane stayed broken until the tab itself was closed.
 *
 * At the measured ~1.8 KB/mission this cap keeps the key well under 0.5 MB.
 */
export const MAX_PERSISTED_MISSIONS = 200;

/** States whose slots must SURVIVE the cap regardless of age — a running or
 *  parked mission is exactly what a reload must not lose. Terminal rows are
 *  the disposable ones; the server still has them. */
const PERSIST_PRIORITY_STATES: ReadonlySet<string> = new Set([
  "queued",
  "submitted",
  "planning",
  "running",
  "synthesizing",
  "awaiting_user",
]);

/**
 * Bound a mission entry list: keep every in-flight slot, then backfill with the
 * most recent terminal ones until the cap.
 *
 * Sorting by `openedAt` and truncating alone would be wrong — a busy org can
 * push an old-but-still-RUNNING mission past the cut, and losing that on reload
 * is the one case the persisted snapshot exists for.
 */
export function capMissionEntries(
  entries: Array<[string, MissionSlot]>,
  max = MAX_PERSISTED_MISSIONS,
): Array<[string, MissionSlot]> {
  if (entries.length <= max) return entries;
  const priority: Array<[string, MissionSlot]> = [];
  const rest: Array<[string, MissionSlot]> = [];
  for (const e of entries) {
    (PERSIST_PRIORITY_STATES.has(e[1].summary?.state as string) ? priority : rest).push(e);
  }
  rest.sort((a, b) => (b[1].summary?.openedAt ?? 0) - (a[1].summary?.openedAt ?? 0));
  return [...priority, ...rest.slice(0, Math.max(0, max - priority.length))];
}

/** Exported for unit testing of the Map ↔ entries persistence contract. */
export function partializeForPersist(state: MissionsStoreState): {
  missions: Array<[string, MissionSlot]>;
  activeCaseId: string | null;
} {
  return {
    // `liveNodes` is a live-only overlay (and a Map, which would not
    // survive the JSON round-trip anyway) — strip it so a restored
    // session shows the authoritative record rather than a frozen
    // snapshot of some long-finished run's progress frames.
    missions: capMissionEntries(
      Array.from(state.missions.entries()).map(
        ([id, slot]) => [id, stripLiveNodes(slot)] as [string, MissionSlot],
      ),
    ),
    activeCaseId: state.activeCaseId,
  };
}

/**
 * sessionStorage that CANNOT wedge the pane.
 *
 * A `QuotaExceededError` out of `setItem` propagates through zustand's persist
 * middleware into whatever `set()` triggered it — i.e. the poll's ingest — where
 * it was caught and rendered through `pollError` as *"Não foi possível alcançar
 * o serviço de agentes"*. A full disk was thus reported as an unreachable
 * backend: operators went looking at the network for a purely client-side
 * failure, and no amount of reloading helped.
 *
 * Persisting is best-effort by nature, so a write that cannot happen must
 * degrade to "this tab won't restore its pane on reload", never to a fake
 * outage. On quota we drop our own key (the largest thing we own) and retry
 * once; if that still fails we give up quietly and let the poll refill.
 */
function createFailSoftSessionStorage(): StateStorage {
  return {
    getItem: (name) => {
      try {
        return sessionStorage.getItem(name);
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      try {
        sessionStorage.setItem(name, value);
      } catch {
        try {
          sessionStorage.removeItem(name);
          sessionStorage.setItem(name, value);
        } catch {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn(
              `[missions] persist skipped — sessionStorage rejected ${value.length}B for "${name}". ` +
                `The pane keeps working; it just will not restore on reload.`,
            );
          }
        }
      }
    },
    removeItem: (name) => {
      try {
        sessionStorage.removeItem(name);
      } catch {
        /* nothing sensible to do */
      }
    },
  };
}

/** Exported for unit testing. Rebuilds a `Map` from the persisted entries
 *  array, tolerating a missing or malformed blob (so a corrupted
 *  sessionStorage value doesn't lock the user out of the pane). Every
 *  entry is shape-validated so a partial slot (e.g. missing `summary`)
 *  doesn't crash `selectQueuedMissions`' sort on `summary.openedAt`. */
export function mergeFromPersist<S extends MissionsStoreState>(
  persisted: unknown,
  current: S,
): S {
  const p = persisted as
    | {
        missions?: unknown;
        activeCaseId?: string | null;
      }
    | undefined;
  const rawEntries = p?.missions;
  const rawArray: Array<[string, MissionSlot]> = Array.isArray(rawEntries)
    ? (rawEntries as Array<[string, MissionSlot]>)
    : [];
  const entries = rawArray.filter(
    (e): e is [string, MissionSlot] => {
      if (!Array.isArray(e) || e.length !== 2) return false;
      const [id, slot] = e;
      if (typeof id !== "string" || !id) return false;
      if (!slot || typeof slot !== "object") return false;
      const sl = slot as Partial<MissionSlot>;
      if (!sl.summary || typeof sl.summary !== "object") return false;
      if (typeof sl.summary.missionId !== "string") return false;
      if (typeof sl.summary.state !== "string") return false;
      return true;
    },
  );
  return {
    ...current,
    ...(p ?? {}),
    // Cap on the way IN as well, not just on the way out. A tab that already
    // holds a legacy oversized blob (2,856 entries were measured in the wild)
    // would otherwise rehydrate all of it and pay the cost on every load until
    // the next write happened to shrink it. Capping here makes the fix
    // self-healing on the very first load.
    missions: new Map(capMissionEntries(entries)),
  } as S;
}

export const useMissionsStore = create<MissionsStoreState>()(
  persist(
    (set) => ({
      ...INITIAL,

      hydrateFromList: (summaries, opts) =>
        set((s) => {
          // Race guard: a case-scoped response must match the currently
          // active case. Pre-fix this had `s.activeCaseId && ...` which
          // short-circuited when activeCaseId was null (cross-case view)
          // → a late case-scoped response would still prune rows.
          // Contract: the App-level subscriber MUST call
          // `setActiveCaseId(caseId)` before mounting the per-case
          // pane; otherwise per-case polls are silently dropped here.
          if (opts?.caseId && opts.caseId !== s.activeCaseId) {
            return {};
          }
          const next = new Map(s.missions);
          // Prune in-scope rows the server no longer reports.
          if (opts?.caseId) {
            const incoming = new Set(summaries.map((m) => m.missionId));
            for (const [id, slot] of next) {
              if (
                slot.summary?.scope?.caseId === opts.caseId &&
                !incoming.has(id)
              ) {
                next.delete(id);
              }
            }
          }
          for (const m of summaries) {
            next.set(m.missionId, slotFromSummary(m, next.get(m.missionId)));
          }
          return {
            missions: next,
            lastPolledAt: Date.now(),
            pollError: null,
          };
        }),

      upsertMission: (record) =>
        set((s) => {
          // Admission guard, mirroring `applyMissionProgress` below — which
          // has always refused a payload with no usable `missionId` while
          // THIS path accepted anything. That asymmetry was live-caught
          // 2026-08-02: a drill-in resolved with an empty body, we keyed a
          // slot under `undefined` whose summary had every field undefined,
          // and the next render of ANY consumer of `summary.scope.caseId`
          // threw — taking out `CaseHeaderBar` and `TeamOfAgentsPane`
          // together, neither of which had anything to do with that mission.
          // A record we cannot address is not data; dropping it is the whole
          // fix. (The selectors below are hardened too, but that is
          // containment — this is the leak.)
          if (typeof record?.missionId !== "string" || record.missionId === "") {
            return {};
          }
          const next = new Map(s.missions);
          next.set(
            record.missionId,
            slotFromRecord(record, next.get(record.missionId)),
          );
          return { missions: next };
        }),

      applyMissionProgress: (payload) =>
        set((s) => {
          const missionId = payload?.missionId;
          if (typeof missionId !== "string" || missionId === "") return {};
          const slot = s.missions.get(missionId);
          // Unknown mission: ignore. The next list poll hydrates it, and
          // the drill-in then carries the same node data authoritatively.
          if (!slot) return {};
          const nodes = Array.isArray(payload.nodes) ? payload.nodes : [];
          if (nodes.length === 0) return {};
          const at = Date.now();
          const liveNodes = new Map(slot.liveNodes ?? []);
          nodes.forEach((node, order) => {
            if (!node || typeof node.id !== "string" || node.id === "") return;
            liveNodes.set(node.id, {
              label: node.label,
              state: node.state,
              pct: node.pct,
              message: node.message,
              order,
              at,
            });
          });
          const next = new Map(s.missions);
          next.set(missionId, {
            ...slot,
            liveNodes,
            // Keep the header in lockstep: the push carries the mission
            // state too, so the parent glyph flips with its children
            // rather than lagging a tick behind them.
            summary: payload.state
              ? { ...slot.summary, state: payload.state }
              : slot.summary,
          });
          return { missions: next };
        }),

      markCanceling: (missionId) =>
        set((s) => {
          const slot = s.missions.get(missionId);
          if (!slot) return {};
          const next = new Map(s.missions);
          next.set(missionId, { ...slot, isCanceling: true });
          return { missions: next };
        }),

      removeMission: (missionId) =>
        set((s) => {
          if (!s.missions.has(missionId)) return {};
          const next = new Map(s.missions);
          next.delete(missionId);
          return { missions: next };
        }),

      clear: () => set({ ...INITIAL, missions: new Map() }),
      setActiveCaseId: (caseId) => set({ activeCaseId: caseId }),
      setPollError: (msg) => set({ pollError: msg }),
      setLastPolledAt: (ts) => set({ lastPolledAt: ts }),
      focusRow: (rowId) => {
        if (focusClearTimer !== null) {
          clearTimeout(focusClearTimer);
          focusClearTimer = null;
        }
        set({ focusedRowId: rowId });
        focusClearTimer = setTimeout(() => {
          set({ focusedRowId: null });
          focusClearTimer = null;
        }, FOCUS_PULSE_MS);
      },
    }),
    {
      name: "lexide:missions",
      storage: createJSONStorage(() => createTenantScopedStorage(createFailSoftSessionStorage)),
      // Persist a serialisable snapshot only — `Map` is rebuilt in `merge`.
      // Both functions are exported above for direct unit testing of the
      // round-trip contract (the persist middleware's auto-hydrate runs at
      // module load, which is fragile to test through).
      partialize: partializeForPersist,
      merge: mergeFromPersist,
    },
  ),
);

// ─── Selectors (plain functions; use with `useShallow` at hook sites) ─

// "ACTIVE" here means "states that warrant FAST polling cadence" — NOT
// the same as `isActive()` on the server (gating plan §A.2, which means
// "slot-holding"). `awaiting_user` is deliberately EXCLUDED: parked
// missions can sit in the 7-day human-gate window with nothing changing
// from the server's side; FAST polling them would mean ~60k polls/week
// per parked mission with an open tab — pure waste. The Bell badge
// (`selectAwaitingUserCount`) still updates on the SLOW 30s cadence,
// and the user's chat-mediated resume triggers an immediate poll via
// the §Q4 wake / SPA refetch affordance.
const ACTIVE_STATES: ReadonlySet<MissionState> = new Set<MissionState>([
  "queued",
  "submitted",
  "planning",
  "running",
  "synthesizing",
]);

/** All slots whose summary's case-scope matches `caseId`. `undefined`
 *  caseId returns the full set (cross-case org feed). */
export const selectMissionsByCase = (
  s: MissionsState,
  caseId?: string,
): MissionSlot[] => {
  const out: MissionSlot[] = [];
  for (const slot of s.missions.values()) {
    if (!caseId || slot.summary?.scope?.caseId === caseId) out.push(slot);
  }
  return out;
};

/** Number of missions currently in `awaiting_user` state. Drives the
 *  sidebar Bell badge (handoff Part D: "load-bearing PERSISTENT 'agents
 *  need your input' prompt"). Cross-case by default. */
export const selectAwaitingUserCount = (
  s: MissionsState,
  caseId?: string,
): number => {
  let n = 0;
  for (const slot of s.missions.values()) {
    if (caseId && slot.summary?.scope?.caseId !== caseId) continue;
    if (slot.summary.state === "awaiting_user") n++;
  }
  return n;
};

/** FIFO queued missions, sorted by `openedAt` (chronological ULID order
 *  is also FIFO, but openedAt is authoritative if the server ever changes
 *  its sort). */
export const selectQueuedMissions = (
  s: MissionsState,
  caseId?: string,
): MissionSlot[] => {
  const out: MissionSlot[] = [];
  for (const slot of s.missions.values()) {
    if (caseId && slot.summary?.scope?.caseId !== caseId) continue;
    if (slot.summary.state === "queued") out.push(slot);
  }
  return out.sort((a, b) => a.summary.openedAt - b.summary.openedAt);
};

/** True iff any mission is in a state that warrants the FAST polling
 *  cadence. Drives `useMissionsPoll`'s 5s vs 30s switch. */
export const selectHasActiveMissions = (s: MissionsState): boolean => {
  for (const slot of s.missions.values()) {
    if (ACTIVE_STATES.has(slot.summary.state)) return true;
  }
  return false;
};

/** Right-sidebar §I.2 auto-expand discriminator. Returns true when
 *  ANY mission warrants pulling the lawyer's attention to the pane —
 *  the FAST-cadence active states ({queued, submitted, planning,
 *  running, synthesizing}) PLUS `awaiting_user` (which the cadence
 *  set excludes for polling-frequency reasons, but DOES merit
 *  visibility). When this returns true on the first poll AND the
 *  user hasn't manually toggled, the §I.2 effect calls
 *  `setRightSidebarExpandedAuto(true)` to pop the pane open. */
const SHOULD_AUTO_EXPAND_STATES: ReadonlySet<MissionState> = new Set<MissionState>([
  ...ACTIVE_STATES,
  "awaiting_user",
]);

export const selectShouldAutoExpand = (s: MissionsState): boolean => {
  for (const slot of s.missions.values()) {
    if (SHOULD_AUTO_EXPAND_STATES.has(slot.summary.state)) return true;
  }
  return false;
};

export interface MissionStateCounts {
  queued: number;
  running: number;
  awaiting_user: number;
  completed: number;
  failed: number;
  canceled: number;
  other: number;
}

/** Histogram of mission states for the Mode-D summary chip row. */
export const selectStateCounts = (
  s: MissionsState,
  caseId?: string,
): MissionStateCounts => {
  const c: MissionStateCounts = {
    queued: 0,
    running: 0,
    awaiting_user: 0,
    completed: 0,
    failed: 0,
    canceled: 0,
    other: 0,
  };
  for (const slot of s.missions.values()) {
    if (caseId && slot.summary?.scope?.caseId !== caseId) continue;
    const st = slot.summary.state;
    if (st === "queued") c.queued++;
    else if (
      st === "submitted" ||
      st === "planning" ||
      st === "running" ||
      st === "synthesizing"
    )
      c.running++;
    else if (st === "awaiting_user") c.awaiting_user++;
    else if (st === "completed") c.completed++;
    else if (st === "failed") c.failed++;
    else if (st === "canceled") c.canceled++;
    else c.other++;
  }
  return c;
};

/** Convenience: extract the `awaiting_user_timeout` reason from a failed
 *  mission, when the SPA has drilled-in to the full record. Returns
 *  `null` when the slot is summary-only or the failure has a different
 *  cause — so consumers can render the special copy unconditionally
 *  (`!!cause`) without a second null-check. */
export const selectAwaitingUserTimeoutCause = (
  slot: MissionSlot,
): NodeError | null => {
  const r = slot.record;
  if (!r) return null;
  if (r.state !== "failed") return null;
  if (r.error?.code !== "awaiting_user_timeout") return null;
  return r.error;
};

// ─── DEV-only test accessor (mirrors `useActiveContextStore`) ─────────

declare global {
  interface Window {
    __missionsStore?: typeof useMissionsStore;
  }
}

if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__missionsStore", {
    value: useMissionsStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
